/**
 * Feature 12 — Vendor 3-step setup wizard.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services/auth';
import { vendorApi } from '../../services/vendor';
import { useAuthStore } from '../../stores/authStore';


function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">
      Step {current} of {total}
    </div>
  );
}


export default function VendorOnboarding() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  const [step, setStep] = useState(1);

  const { data: orgRes } = useQuery({
    queryKey: ['vendor', 'organization'],
    queryFn: () => vendorApi.getOrganization().then((r) => r.data),
  });
  const [orgFields, setOrgFields] = useState({
    name: '', city: '', state: '', address: '', phone: '',
  });
  const [orgLoaded, setOrgLoaded] = useState(false);
  if (orgRes && !orgLoaded) {
    setOrgFields({
      name: orgRes.name || '',
      city: orgRes.city || '',
      state: orgRes.state || '',
      address: orgRes.address || '',
      phone: orgRes.phone || '',
    });
    setOrgLoaded(true);
  }
  const orgUpdate = useMutation({
    mutationFn: () => vendorApi.updateOrganization(orgFields),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor', 'organization'] });
      setStep(2);
    },
  });

  // Step 2 — first guard (skippable).
  const [guard, setGuard] = useState({
    first_name: '', last_name: '', external_id: '', phone: '',
  });
  const guardCreate = useMutation({
    mutationFn: () => vendorApi.createGuard(guard),
    onSuccess: () => setStep(3),
  });

  const finishMutation = useMutation({
    mutationFn: () => authService.completeSetup(),
    onSuccess: (data) => {
      // Patch the user in-place so the banner + redirect see the new
      // timestamp synchronously.  Avoids a getMe() round-trip that
      // sometimes races the navigate.
      const current = useAuthStore.getState().user;
      if (current) {
        setUser({ ...current, setup_completed_at: data.setup_completed_at });
      }
      navigate('/vendor', { replace: true });
    },
  });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 py-16">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <StepIndicator current={step} total={3} />

        {step === 1 && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900">Welcome to GuardGrade</h1>
            <p className="mt-2 text-sm text-slate-600">
              Confirm your organization details so clients see the right
              information when browsing the directory.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(['name', 'city', 'state', 'address', 'phone'] as const).map((f) => (
                <label key={f} className={f === 'name' || f === 'address' ? 'sm:col-span-2' : ''}>
                  <span className="text-xs font-medium text-slate-600 capitalize">{f}</span>
                  <input
                    value={orgFields[f] || ''}
                    onChange={(e) => setOrgFields({ ...orgFields, [f]: e.target.value })}
                    className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                  />
                </label>
              ))}
            </div>
            <div className="mt-6">
              <button
                onClick={() => orgUpdate.mutate()}
                disabled={orgUpdate.isPending || !orgFields.name.trim()}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-brand-600"
              >
                {orgUpdate.isPending ? 'Saving…' : 'Continue'}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900">Add your first guard</h1>
            <p className="mt-2 text-sm text-slate-600">
              You can add more later from the Guards page.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(['first_name', 'last_name', 'external_id', 'phone'] as const).map((f) => (
                <label key={f}>
                  <span className="text-xs font-medium text-slate-600 capitalize">
                    {f.replace('_', ' ')}{f === 'external_id' || f === 'phone' ? ' (optional)' : ''}
                  </span>
                  <input
                    value={(guard as Record<string, string>)[f] || ''}
                    onChange={(e) => setGuard({ ...guard, [f]: e.target.value })}
                    className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                  />
                </label>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={() => guardCreate.mutate()}
                disabled={guardCreate.isPending || !guard.first_name.trim() || !guard.last_name.trim()}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-brand-600"
              >
                {guardCreate.isPending ? 'Adding…' : 'Add Guard'}
              </button>
              <button onClick={() => setStep(3)} className="text-sm text-slate-500 underline hover:text-slate-700">
                Skip for now
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900">You're set up!</h1>
            <p className="mt-2 text-sm text-slate-600">Here's what to do next:</p>
            <div className="mt-6 grid grid-cols-1 gap-3">
              <Link to="/directory" target="_blank" rel="noopener noreferrer" className="block rounded-xl border border-slate-200 p-4 hover:bg-slate-50">
                <div className="text-sm font-semibold text-slate-800">Browse the vendor directory</div>
                <div className="text-xs text-slate-500">Get found by clients searching for security partners.</div>
              </Link>
              <div className="block rounded-xl border border-slate-200 p-4">
                <div className="text-sm font-semibold text-slate-800">Wait for a client to assign you to a site</div>
                <div className="text-xs text-slate-500">Active clients can invite you as a partner from their dashboard.</div>
              </div>
              <Link to="/vendor/guards" className="block rounded-xl border border-slate-200 p-4 hover:bg-slate-50">
                <div className="text-sm font-semibold text-slate-800">Add more guards</div>
                <div className="text-xs text-slate-500">Open the Guards page to add the rest of your team.</div>
              </Link>
            </div>
            <div className="mt-6">
              <button
                onClick={() => finishMutation.mutate()}
                disabled={finishMutation.isPending}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-brand-600"
              >
                {finishMutation.isPending ? 'Saving…' : 'Go to dashboard'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
